/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/events-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-events-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/events-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, rodeo_id, event_type_id, judge_sheet_template_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, draw_results_visible, scoring_state, show_results, is_current_live_event, prize_allocation_mode, dynamic_prize_per_entry, entry_fee_prize_portion, display_prize_to_contestants, default_adjustment_overrides, entry_fee_prize_portion_is_percentage, entry_fee_prize_portion_is_fee_deduction, show_prize_amounts_before_complete, show_prize_amounts_in_results, enforce_balanced_positions
 * - Readonly fields: id, trig_event_type_name, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/events-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-events-api-client.ts (API client)
 * - client/api/generated_schemas/events.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T20:50:53.140Z
 * Source config: config/entities/events-entity-config.ts
 * Public fields: 71/84 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EventsClient, EventsQueryOptions } from '../generated_clients/generated-events-api-client.js';
import {
  EventsInsert,
  EventsUpdate,
  EventsFieldConfig
} from '../../types/generated_schemas/generated-events-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/events_business_hooks/

// Query keys with enhanced caching strategy
export const eventsKeys = {
  all: ['events'] as const,
  lists: () => [...eventsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...eventsKeys.lists(), { filters }] as const,
  details: () => [...eventsKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...eventsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...eventsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...eventsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...eventsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useEventsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new EventsClient();
};

/**
 * Hook to get all events with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with events data
 */
export const useEventsList = (options?: EventsQueryOptions) => {
  const client = useEventsClient();
  
  return useQuery({
    queryKey: eventsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single event by ID
 *
 * @param id - The event ID
 * @returns React Query result with single event data
 */
export const useEvents = (id: string) => {
  const client = useEventsClient();
  
  return useQuery({
    queryKey: eventsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/events
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/events_business_hooks/
 */

/**
 * Hook to create a new event
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, draw_results_visible, scoring_state, show_results, is_current_live_event, prize_allocation_mode, dynamic_prize_per_entry, entry_fee_prize_portion, display_prize_to_contestants, default_adjustment_overrides, entry_fee_prize_portion_is_percentage, entry_fee_prize_portion_is_fee_deduction, show_prize_amounts_before_complete, show_prize_amounts_in_results, enforce_balanced_positions
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, trig_event_type_name, created_at, updated_at
 *
 * @returns React Query mutation for creating event
 */
export const useCreateEvents = () => {
  const client = useEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: EventsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    },
  });
};

/**
 * Hook to update an existing event
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, trig_event_type_name, created_at, updated_at
 *
 * @returns React Query mutation for updating event
 */
export const useUpdateEvents = () => {
  const client = useEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EventsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a event
 *
 * @returns React Query mutation for deleting event
 */
export const useDeleteEvents = () => {
  const client = useEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    },
  });
};

/**
 * Hook to count events with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, rodeo_id, event_type_id, judge_sheet_template_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useEventsCount = (filter?: Partial<Record<typeof EventsFieldConfig.filterable[number], any>>) => {
  const client = useEventsClient();
  
  return useQuery({
    queryKey: eventsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useEventsFieldConfig = () => {
  return EventsFieldConfig;
};